import React, { useState } from 'react';
import { X, Play, Database, AlertCircle, Loader2, Filter, Search as SearchIcon } from 'lucide-react';
import { apiService } from '../../../../../components/api/apiService';

interface Props {
    onClose: () => void;
    datastoreId: string;
    connectionName: string;
    onSubmitTemplate: (metadataJson: string) => void;
}

/** Normalize API response to { columns: string[], rows: any[][] } */
function normalizeQueryResult(data: unknown): { columns: string[]; rows: any[][] } {
    const fromArrayOfObjects = (items: any[]): { columns: string[]; rows: any[][] } => {
        if (!items || items.length === 0) return { columns: [], rows: [] };
        const colSet = new Set<string>();
        for (const row of items) {
            if (row && typeof row === 'object' && !Array.isArray(row)) {
                Object.keys(row as Record<string, unknown>).forEach(k => colSet.add(k));
            }
        }
        const columns = Array.from(colSet);
        const rows = items.map(row =>
            columns.map(col => (row && row[col] != null ? String(row[col]) : ''))
        );
        return { columns, rows };
    };

    if (data == null) return { columns: [], rows: [] };

    // Direct array of row objects
    if (Array.isArray(data)) {
        return fromArrayOfObjects(data);
    }

    if (typeof data === 'object' && data !== null) {
        // Shape: { rows: [...] }
        if ('rows' in data) {
            const rowsVal = (data as any).rows;
            if (Array.isArray(rowsVal)) {
                return fromArrayOfObjects(rowsVal);
            }
        }
        // Shape: { columns: [...], rows: [...] }
        if ('columns' in data && 'rows' in data) {
            const d = data as { columns: string[]; rows: any[][] };
            return {
                columns: d.columns || [],
                rows: Array.isArray(d.rows) ? d.rows : []
            };
        }
        // Shape: { data: [...] }
        if ('data' in data) {
            const arr = (data as { data: unknown[] }).data;
            return normalizeQueryResult(arr);
        }
    }

    return { columns: [], rows: [] };
}

const formatSql = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    // Very lightweight formatting: line-break on main clauses
    return trimmed
        .replace(/\\s+/g, ' ')
        .replace(/\\bselect\\b/i, 'SELECT')
        .replace(/\\bfrom\\b/i, '\\nFROM')
        .replace(/\\bwhere\\b/i, '\\nWHERE')
        .replace(/\\bgroup by\\b/i, '\\nGROUP BY')
        .replace(/\\border by\\b/i, '\\nORDER BY')
        .replace(/\\blimit\\b/i, '\\nLIMIT');
};

const QueryBuilderModal: React.FC<Props> = ({ onClose, datastoreId, connectionName, onSubmitTemplate }) => {
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<{ columns: string[]; rows: any[][] } | null>(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [filterColumn, setFilterColumn] = useState<string>('');
    const [filterValue, setFilterValue] = useState('');

    const filteredResult = React.useMemo(() => {
        if (!result) return null;
        let rows = result.rows;

        if (filterColumn && filterValue) {
            const colIndex = result.columns.indexOf(filterColumn);
            if (colIndex !== -1) {
                const fv = filterValue.toLowerCase();
                rows = rows.filter(r =>
                    String(r[colIndex] ?? '').toLowerCase().includes(fv)
                );
            }
        }

        if (globalFilter.trim()) {
            const gf = globalFilter.toLowerCase();
            rows = rows.filter(r =>
                r.some(cell => String(cell ?? '').toLowerCase().includes(gf))
            );
        }

        return { ...result, rows };
    }, [result, globalFilter, filterColumn, filterValue]);

    const handleRun = async () => {
        const q = query.trim();
        if (!q) {
            setError('Enter a query');
            return;
        }
        setError(null);
        setResult(null);
        setGlobalFilter('');
        setFilterColumn('');
        setFilterValue('');
        setLoading(true);
        try {
            const data = await apiService.post(
                `/api/DataStores/${datastoreId}/run-query`,
                { query: q },
                'backend'
            );
            setResult(normalizeQueryResult(data));
        } catch (err: any) {
            setError(err?.message || 'Query failed');
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <div className="relative w-full max-w-4xl max-h-[90vh] bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800 bg-neutral-950/50 shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                            <Database className="w-4 h-4 text-cyan-400" />
                        </div>
                        <div>
                            <h2 className="text-white font-bold">Query Builder</h2>
                            <p className="text-[11px] text-gray-500 mt-0.5">{connectionName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Query input */}
                <div className="px-6 py-4 border-b border-neutral-800 shrink-0">
                    <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider block mb-2">Query</label>
                    <textarea
                        placeholder="SELECT * FROM schema.table LIMIT 100;"
                        className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 font-mono focus:ring-1 focus:ring-cyan-500 outline-none resize-y min-h-[100px] custom-scrollbar"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        disabled={loading}
                    />
                    <div className="flex items-center gap-3 mt-2">
                        <button
                            onClick={handleRun}
                            disabled={loading}
                            className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Play className="w-4 h-4" />
                            )}
                            Run
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const sql = formatSql(query);
                                if (!sql) {
                                    setError('Enter a query before submitting');
                                    return;
                                }
                                const metadata = {
                                    metadata: {
                                        version: '1.0',
                                        generated_at: new Date().toISOString(),
                                        type: 'query_builder',
                                        source: {
                                            id: datastoreId,
                                            display_name: connectionName,
                                            connection_ref: datastoreId
                                        },
                                        query: {
                                            language: 'sql',
                                            text: sql,
                                            columns: (result?.columns || []).map((name) => ({
                                                name,
                                                data_type: 'string'
                                            }))
                                        }
                                    }
                                };
                                const json = JSON.stringify(metadata, null, 2);
                                onSubmitTemplate(json);
                                onClose();
                            }}
                            disabled={loading}
                            className="inline-flex items-center gap-2 bg-emerald-600/90 hover:bg-emerald-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Submit
                        </button>
                        {error && (
                            <span className="inline-flex items-center gap-1.5 text-red-400 text-sm">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                {error}
                            </span>
                        )}
                    </div>
                </div>

                {/* Results + filters */}
                <div className="flex-1 min-h-0 overflow-hidden flex flex-col p-6">
                    <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                        <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                            Results
                        </label>
                        {result && result.columns.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap text-[11px]">
                                <div className="relative">
                                    <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                                    <input
                                        type="text"
                                        value={globalFilter}
                                        onChange={e => setGlobalFilter(e.target.value)}
                                        placeholder="Search all columns..."
                                        className="pl-7 pr-3 py-1.5 rounded-md bg-neutral-800 border border-neutral-700 text-xs text-gray-200 placeholder-gray-500 focus:ring-1 focus:ring-cyan-500 outline-none min-w-[180px]"
                                        disabled={loading}
                                    />
                                </div>
                                <div className="flex items-center gap-1">
                                    <Filter className="w-3.5 h-3.5 text-gray-500" />
                                    <select
                                        value={filterColumn}
                                        onChange={e => setFilterColumn(e.target.value)}
                                        className="bg-neutral-800 border border-neutral-700 rounded-md px-2 py-1 text-xs text-gray-200 focus:ring-1 focus:ring-cyan-500 outline-none"
                                        disabled={loading || !result}
                                    >
                                        <option value="">Column</option>
                                        {result.columns.map(col => (
                                            <option key={col} value={col}>{col}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="text"
                                        value={filterValue}
                                        onChange={e => setFilterValue(e.target.value)}
                                        placeholder="contains..."
                                        className="px-2 py-1 rounded-md bg-neutral-800 border border-neutral-700 text-xs text-gray-200 placeholder-gray-500 focus:ring-1 focus:ring-cyan-500 outline-none min-w-[120px]"
                                        disabled={loading || !filterColumn}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-h-0 rounded-lg border border-neutral-700 bg-neutral-950 overflow-auto custom-scrollbar">
                        {filteredResult === null && !error && !loading && (
                            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                                <Play className="w-10 h-10 mb-2 opacity-40" />
                                <p className="text-sm">Run a query to see results</p>
                            </div>
                        )}
                        {filteredResult && (
                            <table className="w-full border-collapse text-sm">
                                <thead className="sticky top-0 bg-neutral-900 border-b border-neutral-700">
                                    <tr>
                                        {filteredResult.columns.map(col => (
                                            <th key={col} className="text-left px-4 py-2.5 text-gray-400 font-medium whitespace-nowrap border-r border-neutral-700 last:border-r-0">
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredResult.rows.length === 0 && (
                                        <tr>
                                            <td colSpan={filteredResult.columns.length} className="px-4 py-8 text-center text-gray-500">
                                                No rows
                                            </td>
                                        </tr>
                                    )}
                                    {filteredResult.rows.map((row, idx) => (
                                        <tr key={idx} className="border-b border-neutral-800 hover:bg-neutral-800/50">
                                            {row.map((cell, cidx) => (
                                                <td key={cidx} className="px-4 py-2 text-gray-300 whitespace-nowrap max-w-[280px] truncate border-r border-neutral-800 last:border-r-0" title={String(cell)}>
                                                    {cell}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    {filteredResult && (
                        <div className="flex justify-between items-center mt-2 text-[11px] text-gray-500">
                            <span>{filteredResult.rows.length} row(s)</span>
                            {(globalFilter || (filterColumn && filterValue)) && result && (
                                <span>
                                    Showing filtered results from {result.rows.length} total row(s)
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QueryBuilderModal;
